/**
 * About team.
 *
 * Loaded by sections/about-team.liquid with
 *   <script type="module" src="{{ 'about-team.js' | asset_url }}"></script>
 * ES modules are deduplicated by URL, so several instances of the section on one page still fetch
 * and execute this exactly once.
 *
 * Never append custom code to assets/theme.js — a Prestige update overwrites it.
 *
 * ---------------------------------------------------------------------------------------------
 * The shape of the effect
 * ---------------------------------------------------------------------------------------------
 * The box scrolls up at a contained width, pins when it is centred, then two things happen at once
 * over the pinned scroll:
 *
 *   GROW    — the box opens out from its container width to full width (minus the edge padding),
 *             over the first `grow-length` of the pinned scroll.
 *   SCROLL  — the content inside travels upward by exactly its own overflow, across the WHOLE
 *             pinned scroll. So it is already moving while the box is still opening.
 *
 * Then the pin holds for `hold`, and releases.
 *
 * ---------------------------------------------------------------------------------------------
 * Nothing here causes layout, which is the point
 * ---------------------------------------------------------------------------------------------
 * Per frame this writes exactly two custom properties:
 *
 *   --about-team-close  1 → 0   consumed by `clip-path: inset(...)` on the box       (paint, no layout)
 *   --about-team-shift  0 → -Npx consumed by `translate` on the content              (composite)
 *
 * The box is ALWAYS laid out at its full width; the "small" phase is a clip, not a narrower box.
 * Animating a real width (or margin) instead would re-run layout over the whole content subtree on
 * every scroll frame, which is exactly the jank this store can least afford. The content keeps its
 * own max-width and stays centred, so it never re-wraps as the clip opens — it simply has more of
 * the box's background revealed around it.
 *
 * ⚠️ Do not add a CSS `transition` to either property: both are driven every frame, so a transition
 * would lag behind the scrub and fight it. Softness comes from the JS glide below.
 *
 * ---------------------------------------------------------------------------------------------
 * The scroll length is DERIVED, not a setting
 * ---------------------------------------------------------------------------------------------
 * #measure() writes --about-team-scroll-length as `max(grow distance, content overflow ÷ speed)`, so adding
 * a block lengthens the section instead of making everything scroll faster — the lesson recorded for
 * scrolltrigger-word-steps ("scroll length is PER STEP"), applied to a continuous list. At the
 * default speed the content moves at EXACTLY page-scroll rate, so it reads like ordinary scrolling
 * that happens to be taking place inside a box.
 *
 * Because of that, the ResizeObserver watches the BOX and the CONTENT but deliberately NOT the host:
 * the host's height is derived from what we write, so observing it would feed our own writes back in
 * as a resize. The write is also skipped when the value has not materially changed, as a second
 * guard.
 *
 * ---------------------------------------------------------------------------------------------
 * Visibility contract — the part that must not be broken
 * ---------------------------------------------------------------------------------------------
 * --about-team-close and --about-team-shift both default to "fully open, not moved". The spacer's height, the
 * box's fixed height, its `overflow: hidden`, its clip and the content's translate are ALL gated in
 * CSS behind [data-active], which only this file ever sets, and only once the effect is wired up. If
 * the script fails to load or bails, the section renders as a plain contained box with every block
 * stacked inside it and visible — never a cropped one, and never a stretch of dead scroll.
 *
 * ⚠️ The `overflow: hidden` gate matters more here than anywhere else in the theme: ungated, a fixed
 * box height would silently crop every block past the first screenful out of existence. That exact
 * bug already happened once in scrolltrigger-collection-cards.
 */

import { scroll } from 'vendor';
import { matchesMediaQuery } from 'theme';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const readNumber = (element, attribute, fallback) => {
  const value = parseFloat(element.getAttribute(attribute));
  return Number.isFinite(value) ? value : fallback;
};

// Close enough to the target that another frame would not move anything visibly.
const SETTLE_EPSILON = 0.0005;

// A dropped frame (tab in the background, long task) must not let the glide jump the whole gap.
const MAX_FRAME_MS = 100;

// Below this, rewriting the derived scroll length is not worth the layout it would cost — and it is
// what stops a write→resize→write cycle from ever becoming a loop.
const LENGTH_EPSILON = 1;

class AboutTeam extends HTMLElement {
  #abortController;
  #resizeObserver = null;
  #stopScroll = null;
  #started = false;

  #pin = null;
  #box = null;
  #content = null;

  // "Slide over the section above" — our own .shopify-section wrapper and the one before it.
  #section = null;
  #previous = null;
  #overlapObserver = null;

  #pinHeight = 0;
  #pinTop = 0;

  // How far the content has to travel, and how much scrolling the grow gets.
  #maxShift = 0;
  #growLength = 0;
  #scrollLength = 0;

  // Where the scrollbar says we are, and where the effect has actually eased to.
  #targetProgress = 0;
  #smoothProgress = 0;

  // Time constant of the glide, in ms. 0 means "no smoothing" — paint the target directly.
  #tau = 0;

  // Extra pinned scroll AFTER the content has finished, as a fraction of the animated scroll length.
  #holdFraction = 0;
  #rafId = null;
  #lastFrameTime = 0;

  // Content travel per pixel scrolled, as a multiplier. 1 ≈ page-scroll rate.
  #speed = 1;

  // Set while the merchant has a block selected in the theme editor: the box is held fully open and
  // the content parked on that block, so they can see what they are editing.
  #forcedProgress = null;
  #forceOpen = false;

  connectedCallback() {
    this.#abortController = new AbortController();

    // Set up FIRST, and outside both guards below: sliding over the section above is independent of
    // the scroll effect, and it is stacking rather than motion, so it applies even when the effect is
    // switched off or the visitor asks for reduced motion.
    this.#setupOverlap(this.#abortController.signal);

    // The merchant can switch the effect off; the section is then a plain contained box.
    if (!this.hasAttribute('effect')) return;

    // Reduced motion: the section's CSS pins nothing, clips nothing and moves nothing in that case,
    // so there is no state to undo — leave every block visible in normal flow.
    if (!matchesMediaQuery('motion-safe')) return;

    this.#pin = this.querySelector('.about-team__pin');
    this.#box = this.querySelector('.about-team__box');
    this.#content = this.querySelector('.about-team__content');

    if (!this.#pin || !this.#box || !this.#content) return;

    const { signal } = this.#abortController;

    // ⚠️ The host is deliberately NOT observed — see the note at the top of this file. The box
    // catches viewport height changes (its own height is viewport-derived) and the content catches
    // text re-wrapping as the viewport narrows, which between them is everything that matters.
    this.#resizeObserver = new ResizeObserver(() => {
      this.#measure();
      this.#snap();
    });
    this.#resizeObserver.observe(this.#box);
    this.#resizeObserver.observe(this.#content);

    if (window.Shopify?.designMode) {
      this.addEventListener(
        'shopify:block:select',
        (event) => {
          const item = event.target.closest('[data-team-item]');
          this.#forceOpen = true;
          this.#forcedProgress = item ? this.#progressForItem(item) : 1;
          this.#stopTicking();
          this.#paint();
        },
        { signal }
      );

      this.addEventListener(
        'shopify:block:deselect',
        () => {
          this.#forceOpen = false;
          this.#forcedProgress = null;
          this.#snap();
        },
        { signal }
      );
    }

    this.#start();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#teardownOverlap();
    this.#teardown();
  }

  /**
   * "Slide over the section above": hold the previous section still and let this one scroll up over
   * the top of it. The CSS does the holding and the stacking; all this does is decide WHERE the held
   * section stops, and flip the attribute that switches the whole thing on.
   *
   * ⚠️ This is the only place the section writes to a DOM node it does not own, so every change made
   * here has to be undone in #teardownOverlap() — the theme editor re-inserts sections constantly, and
   * a stray inline `top` left on a neighbour would outlive the section that put it there.
   */
  #setupOverlap(signal) {
    if (!this.hasAttribute('overlap')) return;

    this.#section = this.closest('.shopify-section');

    if (!this.#section || !this.#resolvePrevious()) return;

    // Only now does the CSS make the neighbour sticky and lift this section over it.
    this.#section.setAttribute('data-overlap', '');
    this.#measureOverlap();

    // The neighbour's own height decides where it stops, so watch it rather than assuming.
    this.#overlapObserver = new ResizeObserver(() => this.#measureOverlap());
    this.#overlapObserver.observe(this.#previous);

    // The stop position is viewport-relative, so it moves when the window does.
    window.addEventListener('resize', () => this.#measureOverlap(), { signal, passive: true });

    if (window.Shopify?.designMode) {
      // Editing the neighbour replaces its element, taking our inline `top` with it and leaving us
      // holding a detached node. Re-resolve rather than re-measuring a corpse.
      document.addEventListener(
        'shopify:section:load',
        () => {
          this.#resolvePrevious();
          this.#measureOverlap();
        },
        { signal }
      );
    }
  }

  /** Point #previous at the current preceding section, moving the observer and cleaning up the old. */
  #resolvePrevious() {
    const previous = this.#section?.previousElementSibling;

    if (!previous?.classList.contains('shopify-section')) return false;
    if (previous === this.#previous) return true;

    if (this.#previous) {
      this.#overlapObserver?.unobserve(this.#previous);
      this.#previous.style.removeProperty('top');
    }

    this.#previous = previous;
    this.#overlapObserver?.observe(previous);

    return true;
  }

  #measureOverlap() {
    if (!this.#previous) return;

    // ⚠️ min(0, …), not 0. Sticking the neighbour the moment its top reaches the top of the screen
    // would freeze a section taller than the viewport with its lower half never seen. A negative
    // offset instead lets it scroll all the way in, then holds it with its bottom on the bottom of
    // the screen — so nothing is skipped, whatever is above.
    const top = Math.min(0, document.documentElement.clientHeight - this.#previous.offsetHeight);

    this.#previous.style.top = `${Math.round(top)}px`;
  }

  #teardownOverlap() {
    this.#overlapObserver?.disconnect();
    this.#overlapObserver = null;
    this.#section?.removeAttribute('data-overlap');
    this.#previous?.style.removeProperty('top');
    this.#previous = null;
    this.#section = null;
  }

  #start() {
    if (this.#started) return;

    this.#build();

    // Only now does the CSS pin the box, fix its height, clip it and start moving the content.
    // Setting this last means a failure anywhere above leaves the plain, readable fallback in place.
    this.setAttribute('data-active', '');

    // Measure once the box is actually at its pinned height, then hand over to the scroll loop.
    // scroll() with a bare callback fires on every scroll frame, rAF-throttled by Motion One.
    this.#measure();
    this.#snap();
    this.#stopScroll = scroll(() => this.#syncTarget());

    this.#started = true;
  }

  #teardown() {
    this.#stopScroll?.();
    this.#stopScroll = null;
    this.#stopTicking();
    this.#started = false;
    this.removeAttribute('data-active');

    this.style.removeProperty('--about-team-close');
    this.style.removeProperty('--about-team-shift');
    this.style.removeProperty('--about-team-scroll-length');
  }

  #build() {
    this.#tau = clamp(readNumber(this, 'smoothing', 35), 0, 100) * 4;

    // A fraction rather than a length, so one value works at both breakpoints even though their
    // scroll lengths differ.
    this.#holdFraction = clamp(readNumber(this, 'hold', 0), 0, 1);

    // Guarded above 0 so a merchant cannot divide the travel by zero and freeze the content.
    this.#speed = clamp(readNumber(this, 'speed', 100), 10, 400) / 100;
  }

  #measure() {
    const viewport = document.documentElement.clientHeight;

    // offsetHeight, not getBoundingClientRect(): the content carries a translate, and offsetHeight
    // reports the laid-out height regardless of transforms.
    this.#pinHeight = this.#pin.offsetHeight;

    const boxHeight = this.#box.offsetHeight;
    const contentHeight = this.#content.offsetHeight;

    // Nothing to scroll if the content already fits — the box then just grows and releases.
    this.#maxShift = Math.max(0, contentHeight - boxHeight);

    this.#growLength = (clamp(readNumber(this, 'grow-length', 60), 0, 300) / 100) * viewport;

    // ⚠️ max(), not grow + travel. The content moves across the WHOLE pinned scroll, so its rate is
    // maxShift / scrollLength — and adding the grow distance on top would dilute that, leaving the
    // content always slower than the speed the merchant asked for and only approaching it as the
    // content got longer. Taking the larger of the two instead means the rate is EXACTLY `speed`
    // for any content longer than the grow, and never exceeds it for content shorter than the grow
    // (where the box still gets its full opening distance and the content just drifts across it).
    this.#scrollLength = Math.max(this.#growLength, this.#maxShift / this.#speed);

    // The spacer's height, and therefore the whole section's height, comes from this. Skipping an
    // unchanged write is what keeps the resize → measure → write → resize path from looping.
    const previous = parseFloat(this.style.getPropertyValue('--about-team-scroll-length'));
    if (!Number.isFinite(previous) || Math.abs(previous - this.#scrollLength) > LENGTH_EPSILON) {
      this.style.setProperty('--about-team-scroll-length', `${this.#scrollLength.toFixed(1)}px`);
    }

    // The resolved `top` of the sticky element — the offset it parks at. CSS owns this one (it is a
    // pure expression: half the leftover viewport, so the box lands centred), so it is read back
    // rather than recomputed here and the two definitions cannot drift. Must be read AFTER
    // [data-active] is set: until the pin is actually sticky its computed `top` is `auto`.
    const pinTop = parseFloat(getComputedStyle(this.#pin).top);
    this.#pinTop = Number.isFinite(pinTop) ? pinTop : 0;
  }

  /** The progress that parks a given block in the middle of the box. Editor convenience only. */
  #progressForItem(item) {
    if (this.#maxShift <= 0) return 1;

    const wanted = item.offsetTop + item.offsetHeight / 2 - this.#box.offsetHeight / 2;
    return clamp(wanted / this.#maxShift, 0, 1);
  }

  #readProgress() {
    const rect = this.getBoundingClientRect();

    // The host stays in flow and is the scroll spacer: the pin, plus .about-team__spacer underneath it.
    // That surplus over the pin is how far the page scrolls while the pin is stuck — but the content
    // must finish before the hold begins, so dividing by (1 + hold) recovers the animated length on
    // its own. Progress then reaches 1 at the end of that length and the clamp holds it there
    // through the hold, leaving the finished box on screen instead of releasing on the last frame.
    const animated = (rect.height - this.#pinHeight) / (1 + this.#holdFraction);

    return animated > 0 ? clamp((this.#pinTop - rect.top) / animated, 0, 1) : 1;
  }

  /** Scroll (or a resize) moved the target. Glide toward it, or jump if smoothing is off. */
  #syncTarget() {
    this.#targetProgress = this.#readProgress();

    if (this.#tau <= 0) {
      this.#smoothProgress = this.#targetProgress;
      this.#paint();
      return;
    }

    this.#startTicking();
  }

  /** Land on the current scroll position immediately, with no glide. */
  #snap() {
    this.#stopTicking();
    this.#targetProgress = this.#readProgress();
    this.#smoothProgress = this.#targetProgress;
    this.#paint();
  }

  #startTicking() {
    if (this.#rafId !== null) return;
    this.#lastFrameTime = 0;
    this.#rafId = requestAnimationFrame(this.#tick);
  }

  #stopTicking() {
    if (this.#rafId === null) return;
    cancelAnimationFrame(this.#rafId);
    this.#rafId = null;
    this.#lastFrameTime = 0;
  }

  // Runs only while there is still ground to cover — it stops itself once it settles, so there is no
  // permanent rAF loop burning battery on a page nobody is scrolling.
  #tick = (now) => {
    this.#rafId = null;

    const elapsed = this.#lastFrameTime ? Math.min(now - this.#lastFrameTime, MAX_FRAME_MS) : 16.7;
    this.#lastFrameTime = now;

    // Frame-rate independent easing: over a fixed wall-clock time the glide covers the same ground
    // whether the display runs at 60Hz or 120Hz. A plain `+= gap * 0.1` would not.
    const alpha = 1 - Math.exp(-elapsed / this.#tau);
    this.#smoothProgress += (this.#targetProgress - this.#smoothProgress) * alpha;

    if (Math.abs(this.#targetProgress - this.#smoothProgress) < SETTLE_EPSILON) {
      this.#smoothProgress = this.#targetProgress;
      this.#paint();
      this.#lastFrameTime = 0;
      return;
    }

    this.#paint();
    this.#rafId = requestAnimationFrame(this.#tick);
  };

  #paint() {
    const progress = this.#forcedProgress === null ? this.#smoothProgress : this.#forcedProgress;

    // The grow finishes after `growLength` PIXELS of the pinned scroll, not after a share of it —
    // so lengthening the section by adding blocks does not also slow the opening down.
    const grown =
      this.#forceOpen || this.#growLength <= 0
        ? 1
        : clamp((progress * this.#scrollLength) / this.#growLength, 0, 1);

    // Linear in both, deliberately: the glide above is what softens the motion. Easing here as well
    // would compound into a sluggish, rubbery feel.
    this.style.setProperty('--about-team-close', (1 - grown).toFixed(4));
    this.style.setProperty('--about-team-shift', `${(-this.#maxShift * progress).toFixed(2)}px`);
  }
}

if (!window.customElements.get('about-team')) {
  window.customElements.define('about-team', AboutTeam);
}
